/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import { ofType } from 'redux-observable';
import { empty, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { ActionManagerInstance } from '../../actions/manager';
import { publicConstants } from '../../constants';
import { getResolvedTargetName } from '../../router/routing-manager';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import { throttleUntilChanged } from '../../utils/rxjs-utils';
import Utils from '../../utils/utils';
import * as actionType from '../../store/state/actions';
import { getRootContainerName, isContainerSingleMode } from '../../container/container-utils';
import {
  addPreviewResetAction,
  dispatchCorrectActionIfChildOrRemote,
  getActionBasedOnContainerMode,
  getActionPayload,
  getContext,
  getDirtyDetails,
  isAddContainerAction,
  processDataToDispatchErrorAction,
  processViewResponse
} from '../../store/state/epics/util';
import {
  addActionToRemoveContainerItem,
  getCaseClassName,
  getCaseInfoID,
  getDisplayCaseID,
  isModalContainer,
  updateKeyAndFlowName,
  promiseHandler,
  getCaseFirstAssignmentID,
  isCoexistenceCase,
  canLaunchCaseInModal,
  getCurrentCaseViewMode
} from '../common-helper';
import { getSemanticURL } from '../../router/app-router';
import EnvironmentInfo from '../../environment-info';
import BroadcastUtils from '../../utils/broadcast-utils';
import { isAssignmentInCreateStage } from '../../utils/case-utils';
import type { Action } from '../../store/state/state-manager';
import type { Actionout } from '../../types';
import { getToasterConfig } from '../../object/common-helper';
import NavigationUtils from '../../utils/navigation-utils';
import HeaderProcessor from '../../service_broker/header-processor';
import CaseUtils from '../utils';

import {
  getContainerName,
  evaluateDirtyBeforeDxApiCall,
  setDirectUrlUpdation,
  STATE_PATHS,
  isInCreateStage,
  getCaseViewMode
} from './helper';
import { executeCreateWorkRequest } from './async-request';
import { CREATE_WORK } from './action-creator';
import {
  publishDisplayCreatedCaseToasterEvent,
  publishCreateStageDoneEvent,
  publishCaseViewOpenedAfterCreateEvent
} from './events';

const createWork = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: Actionout[] = [];
  let toastMessage = {};
  let isTraditionalUICase = false;

  return action$.pipe(
    ofType(CREATE_WORK),
    throttleUntilChanged(100),
    map((action: Action) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((payload) => {
      const { context } = payload;
      const containerName = getContainerName(payload);
      const target = getResolvedTargetName(containerName, context);
      const { continueExecution, removeActionPayload } = evaluateDirtyBeforeDxApiCall(target);
      isTraditionalUICase = isCoexistenceCase(payload.class);

      if (!continueExecution) {
        ActionManagerInstance.rejectActionByID(actionMgrID);
        return empty();
      }
      payload.isTraditionalUICase = isTraditionalUICase;
      return executeCreateWorkRequest(payload, { removeActionPayload });
    }),
    mergeMap(async (data) => {
      if (data && data.type === actionType.ERROR) {
        data.payload.context = getRootContainerName();
        actionsOut.push(processDataToDispatchErrorAction(data));
        return actionsOut;
      }

      const { actionInPayload, response, headers, removeActionPayload } = data;
      let isInCreateStageRes;
      if (actionInPayload.renderInline || EnvironmentInfo.getDefaultCasePage() || isTraditionalUICase) {
        isInCreateStageRes = false;
      } else {
        isInCreateStageRes = isInCreateStage(response);
      }
      // When a case is created it will be in first assignment
      Utils.setIn(response.data, publicConstants.CASE_INFO.IS_FIRST_ASSIGNMENT, true);
      const containerName = getContainerName({ ...actionInPayload, isInCreateStage: isInCreateStageRes });
      const target = getResolvedTargetName(containerName, actionInPayload.context);
      const context = getContext(target, containerName, actionInPayload.context);

      if (!isModalContainer(containerName) && isContainerSingleMode(target)) {
        const { continueExecution } = getDirtyDetails(target);
        if (!continueExecution) {
          ActionManagerInstance.rejectActionByID(actionMgrID);
          return empty();
        }
      }
      const openCaseViewRes = actionInPayload?.openCaseViewAfterCreate ?? true;

      if (!openCaseViewRes && !isInCreateStageRes) {
        const assignmentId = getCaseFirstAssignmentID(response);
        const { businessID, caseType, resolvedURL } = getToasterConfig(response);
        toastMessage = {
          id: businessID,
          label: caseType,
          href: resolvedURL
        };
        publishDisplayCreatedCaseToasterEvent(toastMessage);
        publishCreateStageDoneEvent({ caseId: businessID, assignmentId, caseType });
        return actionsOut;
      }
      const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);

      const ID = String(getDisplayCaseID(getCaseInfoID(translatedDXAPIResponse)));
      let semanticURL = getSemanticURL('createWork', { caseClassName: getCaseClassName(translatedDXAPIResponse) });
      semanticURL = semanticURL.replace(/{.+?}/, ID);
      translatedDXAPIResponse.semanticURL = semanticURL;

      NavigationUtils.setComponentCache(ID, HeaderProcessor.addDebugHeaders(actionInPayload.debugInfo));
      setDirectUrlUpdation(translatedDXAPIResponse, actionInPayload);

      const options = { acName: target, context };
      const currentCaseViewMode = getCurrentCaseViewMode(context as string);

      await processViewResponse(translatedDXAPIResponse, actionInPayload, options);
      const caseViewMode = getCaseViewMode(translatedDXAPIResponse);

      const commonInfo = {
        target,
        context,
        containerItemID: context,
        headers,
        semanticURL,
        isInCreateStage: isInCreateStageRes,
        isMinimizable: isInCreateStageRes,
        caseViewMode,
        currentCaseViewMode,
        assignmentContext: Utils.getIn(translatedDXAPIResponse, STATE_PATHS.NEXT_ASSIGNMENT_INFO_CONTEXT),
        nextAssignmentInfo: Utils.getIn(translatedDXAPIResponse, STATE_PATHS.NEXT_ASSIGNMENT_INFO),
        resourceType: publicConstants.CASE,
        openCaseViewAfterCreate: actionInPayload.openCaseViewAfterCreate,
        modalOptions: actionInPayload.modalOptions,
        debugInfo: actionInPayload.debugInfo
      };

      // updating key and flowName
      updateKeyAndFlowName(commonInfo, translatedDXAPIResponse);
      const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
      if (isTraditionalUICase) {
        payload.data.caseInfo.content.coexistenceAction = 'openAssignment';
      }
      CaseUtils.setTraditionalGadgetId(payload.data);

      const action = getActionBasedOnContainerMode(target, payload);

      if (isAddContainerAction(action)) {
        addActionToRemoveContainerItem(actionsOut, { removeActionPayload, target });
      }

      actionsOut.push(action);
      addPreviewResetAction(actionsOut);
      publishCaseViewOpenedAfterCreateEvent({ caseId: ID });
      return actionsOut;
    }),
    map(() => {
      const launchCaseInModal = canLaunchCaseInModal();
      const isCaseInCreateStage = isAssignmentInCreateStage(Utils.getIn(actionsOut[0]?.payload, 'data.caseInfo'));
      if (isTraditionalUICase && launchCaseInModal && isCaseInCreateStage) {
        BroadcastUtils.postCreateCaseMessage(
          actionsOut[0]?.payload?.nextAssignmentInfo?.ID,
          dispatchCorrectActionIfChildOrRemote,
          actionsOut
        );
      } else {
        dispatchCorrectActionIfChildOrRemote(actionsOut);
      }
      promiseHandler(actionsOut, actionMgrID);
      toastMessage = {};
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default createWork;
